import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import Login from "@/pages/Login";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Tenders from "@/pages/Tenders";
import Commercial from "@/pages/Commercial";
import Engineering from "@/pages/Engineering";
import Reporting from "@/pages/Reporting";
import EnterpriseIntelligenceHub from "@/pages/edi/Hub";
import EDIDashboard from "@/pages/edi/EDIDashboard";
import BusinessValue from "@/pages/edi/BusinessValue";
import ModuleView from "@/pages/edi/ModuleView";
import CaptureOnce from "@/pages/edi/CaptureOnce";
import ReuseSavings from "@/pages/edi/ReuseSavings";
import KnowledgeReuseDashboard from "@/pages/edi/KnowledgeReuse";
import EnterpriseValue from "@/pages/edi/EnterpriseValue";
import EngineeringProductivity from "@/pages/edi/EngineeringProductivity";
import ExecutiveDemo from "@/pages/edi/ExecutiveDemo";
import BoardBrief from "@/pages/edi/BoardBrief";
import KnowledgeIntelligence from "@/pages/edi/KnowledgeIntelligence";
import ValueCalculator from "@/pages/edi/ValueCalculator";
import IntellectualCapital from "@/pages/edi/IntellectualCapital";
import BoardRoom from "@/pages/edi/BoardRoom";
import DemoCentre from "@/pages/edi/DemoCentre";
import AiAdvisor from "@/pages/edi/AiAdvisor";
import ReuseIntelligence from "@/pages/edi/ReuseIntelligence";
import AcceptanceAudit from "@/pages/edi/AcceptanceAudit";
import Certification from "@/pages/edi/Certification";
import CapabilityMaturity from "@/pages/edi/CapabilityMaturity";
import ClientPortal from "@/pages/edi/ClientPortal";
import VerifyCertificate from "@/pages/VerifyCertificate";

const RequireAuth = ({ children }) => {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--sgsb-bg)]">
                <div className="kicker text-[var(--sgsb-gold)]">Loading SGS Bateman EPCM360…</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    return children;
};

const RedirectIfAuthed = ({ children }) => {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (user) return <Navigate to="/dashboard" replace />;
    return children;
};

function App() {
    return (
        <div className="App sgsb-noise">
            <AuthProvider>
                <BrowserRouter>
                    <Routes>
                        <Route path="/login" element={<RedirectIfAuthed><Login /></RedirectIfAuthed>} />
                        <Route path="/verify/:cert_id" element={<VerifyCertificate />} />
                        <Route
                            path="/"
                            element={
                                <RequireAuth>
                                    <AppShell />
                                </RequireAuth>
                            }
                        >
                            <Route index element={<Navigate to="/dashboard" replace />} />
                            <Route path="dashboard" element={<Dashboard />} />
                            <Route path="projects" element={<Projects />} />
                            <Route path="projects/:id" element={<ProjectDetail />} />
                            <Route path="tenders" element={<Tenders />} />
                            <Route path="commercial" element={<Commercial />} />
                            <Route path="engineering" element={<Engineering />} />
                            <Route path="reporting" element={<Reporting />} />
                            <Route path="edi" element={<EnterpriseIntelligenceHub />} />
                            <Route path="edi/dashboard" element={<EDIDashboard />} />
                            <Route path="edi/business-value" element={<BusinessValue />} />
                            <Route path="edi/reuse-savings" element={<ReuseSavings />} />
                            <Route path="edi/knowledge-reuse" element={<KnowledgeReuseDashboard />} />
                            <Route path="edi/capture-once" element={<CaptureOnce />} />
                            <Route path="edi/enterprise-value" element={<EnterpriseValue />} />
                            <Route path="edi/engineering-productivity" element={<EngineeringProductivity />} />
                            <Route path="edi/executive-demo" element={<ExecutiveDemo />} />
                            <Route path="edi/board-brief" element={<BoardBrief />} />
                            <Route path="edi/knowledge-intelligence" element={<KnowledgeIntelligence />} />
                            <Route path="edi/value-calculator" element={<ValueCalculator />} />
                            <Route path="edi/intellectual-capital" element={<IntellectualCapital />} />
                            <Route path="edi/board-room" element={<BoardRoom />} />
                            <Route path="edi/demo-centre" element={<DemoCentre />} />
                            <Route path="edi/ai-advisor" element={<AiAdvisor />} />
                            <Route path="edi/reuse-intelligence" element={<ReuseIntelligence />} />
                            <Route path="edi/acceptance-audit" element={<AcceptanceAudit />} />
                            <Route path="edi/certification" element={<Certification />} />
                            <Route path="edi/capability-maturity" element={<CapabilityMaturity />} />
                            <Route path="edi/client-portal" element={<ClientPortal />} />
                            <Route path="edi/module/:key" element={<ModuleView />} />
                        </Route>
                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                </BrowserRouter>
                <Toaster
                    position="bottom-right"
                    theme="dark"
                    toastOptions={{
                        style: {
                            background: "#0F172A",
                            border: "1px solid rgba(212, 175, 55, 0.25)",
                            color: "#F8FAFC",
                        },
                    }}
                />
            </AuthProvider>
        </div>
    );
}

export default App;
